import {
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    CircularProgress,
} from '@mui/material';
import { useAsyncData } from '@shared/react';
import { getAccounts } from '@widgets/AccountsTable/api';
import { assignSubAccounts, getbyFdid } from '@widgets/UserManagementTable/api';
import { useEffect, useState } from 'react';
import { MdArrowBack } from 'react-icons/md';
import { useParams, useNavigate } from 'react-router-dom';
import { FinancialProfileStep, GeneralStep } from '@widgets/AccountsTable/components';
import { KYCRequests } from '@widgets/AccountsTable/components/KYCRequests';
import { ClientDetailsTable } from '@widgets/ClientDetails';
import { UnassignClientDetailsTable } from '@widgets/UnassignedClients/ClientDetailsTable';
import useStore from '@widgets/UserManagementTable/lib/store/assign-client-store';
import subAccountRefreshStore from '@widgets/UserManagementTable/lib/store/account-refresh-store';
import './UserDetails.scss';
import OnboardingModal from './../OnboardingModal';

const UserDetails = () => {
    const navigate = useNavigate();
    const { fdid } = useParams<{ fdid: string }>(); // Extract fdid from URL params
    const [assignedAccounts, setAssignedAccounts] = useState<any[]>([]);
    const [selectedAccounts, setSelectedAccounts] = useState<any[]>([]); // New state for multi-select
    const [loading, setLoading] = useState(false); // State for loader
    const [snackbarOpen, setSnackbarOpen] = useState(false); // State for snackbar visibility
    const [snackbarMessage, setSnackbarMessage] = useState(''); // Snackbar message
    const setClientAssigned = useStore((state) => state.setClientAssigned);
    const subAccountRefresh = subAccountRefreshStore();
    const [isOpen, setIsOpen] = useState(true);
    const [openPopup, setOpenPopup] = useState(false);

    const handleSelectChange = (event: any) => {
        const { value } = event.target;
        const updatedSelectedAccounts = SubAccountsToAssign.filter((account: any) => value.includes(account.id));
        setSelectedAccounts(updatedSelectedAccounts);
    };

    const handleSave = async () => {
        setLoading(true); // Start loading
        try {
            const updatedAccounts = [...assignedAccounts, ...selectedAccounts];
            setAssignedAccounts(updatedAccounts);
            await assignSubAccounts(selectedAccounts, editedValues.id);
            setSnackbarMessage('Accounts assigned successfully!');
            setSnackbarOpen(true); // Show success message
            setSelectedAccounts([]); // Clear the selected accounts after assignment
            handleRefresh();
            setClientAssigned(true);
        } catch (error) {
            setSnackbarMessage('Failed to assign accounts!');
            setSnackbarOpen(true); // Show error message
        } finally {
            setLoading(false); // Stop loading
        }
    };

    const handlePopup = () => {
        setOpenPopup(true);
    };

    let [editedValues, setEditedValues] = useState<any>();
    let [userData, setUserData] = useState<any>();

    useEffect(() => {
        async function getAccountData() {
            const a = await getbyFdid(fdid ? fdid : '');
            setUserData(a);
            setEditedValues(a.accounts[0]);
        }
        getAccountData();
    }, []);

    useEffect(() => {
        if (subAccountRefresh) {
            handleRefresh();
        }
    }, [subAccountRefresh]);

    const { data: SubAccountsToAssign, handleRefresh }: any = useAsyncData(
        async () => {
            const { items } = await getAccounts({
                page: 1,
                pageSize: 100,
                filter: 'ParentId=null,AccountType!=RIA',
            });
            return items;
        },
        [], // Empty dependencies array so it runs once
        [],
    );

    return (
        <div className='container'>
            <div className='header-div-user'>
                <div
                    className='back-icon'
                    onClick={() => navigate('/user')}
                >
                    <MdArrowBack size={24} />
                </div>
                <div className='title'>
                    <h1>User Management</h1>
                    <span>Clearing Account: {userData?.accounts[0].clearingAccount}</span>
                </div>
                {editedValues?.accountType === 'RIA' ? (
                    <div style={{ marginLeft: 'auto' }}>
                        <FormControl style={{ width: '363px' }}>
                            <InputLabel
                                id='multi-select-label'
                                style={{ bottom: '24px', top: 'auto' }}
                            >
                                Assign Clients
                            </InputLabel>
                            <Select
                                id='multi-select'
                                labelId='multi-select-label'
                                multiple
                                value={selectedAccounts.map((account: any) => account.id)}
                                onChange={handleSelectChange}
                                input={<OutlinedInput label='Assign Clients' />}
                                style={{ height: '40px' }}
                                renderValue={(selected) => (
                                    <Box sx={{ gap: 0.5 }}>
                                        {selectedAccounts.map((account: any) => (
                                            <Chip
                                                key={account.id}
                                                label={`${account.verificationRequest?.firstName} ${account.verificationRequest?.lastName} {${account.fdid}}`}
                                            />
                                        ))}
                                    </Box>
                                )}
                            >
                                {SubAccountsToAssign?.map((account: any) => (
                                    <MenuItem
                                        key={account.id}
                                        value={account.id}
                                    >
                                        {`${account.verificationRequest?.firstName} ${account.verificationRequest?.lastName} {${account.fdid}}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            className='assign-button'
                            color='primary'
                            onClick={handleSave}
                            disabled={loading || selectedAccounts.length < 1}
                            sx={{ textTransform: 'none' }}
                        >
                            {loading ? (
                                <CircularProgress
                                    size={24}
                                    color='inherit'
                                />
                            ) : (
                                'Assign'
                            )}
                        </Button>
                        <Button
                            className='customer-onboarding-button'
                            color='primary'
                            onClick={() => handlePopup()}
                            sx={{ textTransform: 'none' }}
                        >
                            {loading ? (
                                <CircularProgress
                                    size={24}
                                    color='inherit'
                                />
                            ) : (
                                'Customer Onboarding'
                            )}
                        </Button>
                    </div>
                ) : null}
            </div>

            <div className='main-content'>
                <div className='content-section'>
                    <h1>Account Details</h1>
                    <span>Clearing Account: {userData?.accounts[0].clearingAccount}</span>
                    <hr />
                    {userData && (
                        <GeneralStep
                            editedValues={editedValues}
                            setEditedValues={setEditedValues}
                            account={userData?.accounts[0]}
                            fromDetail={true}
                        />
                    )}
                </div>
                <div className='content-section'>
                    <h1>KYC Requests</h1>
                    <span>Clearing Account: {userData?.accounts[0].clearingAccount}</span>
                    <hr />
                    <KYCRequests clearingAccount={userData?.accounts[0].clearingAccount} />
                </div>
                <div className='content-section'>
                    <h1>Financial Profile</h1>
                    <span>Clearing Account: {userData?.accounts[0].clearingAccount}</span>
                    <hr />
                    {userData && (
                        <FinancialProfileStep
                            editedValues={editedValues}
                            setEditedValues={setEditedValues}
                            account={userData?.accounts[0]}
                            fromDetail={true}
                        />
                    )}
                </div>
            </div>

            <div className='extra-content'>
                {userData?.accounts[0].accountType === 'RIA' && (
                    <ClientDetailsTable
                        userId={parseInt(userData?.accounts[0]?.id)}
                        fromRIA={false}
                    />
                )}
            </div>

            <div className='extra-content'>
                {userData?.accounts[0].accountType === 'RIA' && (
                    <UnassignClientDetailsTable
                        userId={parseInt(userData?.accounts[0]?.id)}
                        fromRIA={false}
                    />
                )}
            </div>

            <OnboardingModal
                key={new Date().getTime()}
                fdId={fdid ?? ''}
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
            />
        </div>
    );
};

export default UserDetails;
